/**
 * Everything the export wizard can put in a spreadsheet, declared once.
 *
 * A FIELD is a value read from one export row (GET /jobs/export/rows). A COLUMN is what the user
 * gets in the file: a field plus the header to print. Presets are ordered column lists, so they can
 * reproduce the exact legacy layouts - including the accounts sheet's repeated columns (Driver and
 * Courier, Weight and Overall Weight) under their historical headers.
 *
 * `needs` names the server-side lookup a field depends on; the wizard only asks the server for the
 * lookups the chosen columns use, which keeps each request lighter.
 * `total: true` marks money/number fields summed by the optional totals row.
 */

const num = (v) => (v === null || v === undefined || v === '' ? '' : Number(v));
const datePart = (ts) => (ts ? String(ts).slice(0, 10) : '');
const timePart = (ts) => (ts && String(ts).length > 11 ? String(ts).slice(11, 19) : '');
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '');
const isDelivered = (r) => String(r.status || '').toUpperCase() === 'DELIVERED';

export const FIELD_GROUPS = [
  'Identifiers', 'Status & type', 'Dates', 'Consignee', 'Shipper / pickup', 'Money',
  'Accounts (delivered only)', 'Driver', 'Shipment', 'Items', 'Billing', 'Customer requests', 'Legacy (always blank)',
];

export const FIELDS = [
  // Identifiers
  { key: 'awb', label: 'AWB', group: 'Identifiers', get: (r) => r.awb },
  { key: 'businessRefNo', label: 'Client Ref', group: 'Identifiers', get: (r) => r.businessRefNo },
  { key: 'masterAwbNumber', label: 'Multishipment AWB', group: 'Identifiers', get: (r) => r.masterAwbNumber },
  { key: 'childAwbs', label: 'Child AWBs', group: 'Identifiers', get: (r) => r.childAwbs },
  { key: 'orderId', label: 'Order ID', group: 'Identifiers', get: (r) => r.orderId },
  { key: 'partnerId', label: 'Partner ID', group: 'Identifiers', get: (r) => r.partnerId },
  { key: 'storeId', label: 'Store', group: 'Identifiers', get: (r) => r.storeId },

  // Status & type
  { key: 'status', label: 'Status', group: 'Status & type', get: (r) => r.status },
  { key: 'legType', label: 'Type', group: 'Status & type', get: (r) => r.legType },
  { key: 'orderTyp', label: 'Store movement', group: 'Status & type', get: (r) => r.orderTyp },
  { key: 'serviceTime', label: 'Service Time', group: 'Status & type', get: (r) => r.serviceTime },
  { key: 'projectName', label: 'Project', group: 'Status & type', get: (r) => r.projectName },
  { key: 'shipmentType', label: 'Shipment Type', group: 'Status & type', get: (r) => r.shipmentType },
  { key: 'deliveryStatusReason', label: 'Status Reason', group: 'Status & type', get: (r) => r.deliveryStatusReason },

  // Dates
  { key: 'orderDate', label: 'Order Date', group: 'Dates', get: (r) => r.orderDate },
  { key: 'assignedAt', label: 'Assigned At', group: 'Dates', get: (r) => r.assignedAt },
  { key: 'assignedDate', label: 'Assigned Date', group: 'Dates', get: (r) => datePart(r.assignedAt) },
  { key: 'scannedAt', label: 'Scan Time', group: 'Dates', get: (r) => r.scannedAt },
  { key: 'createdAt', label: 'Created At', group: 'Dates', get: (r) => r.createdAt },
  { key: 'createdDate', label: 'Order Creation Date', group: 'Dates', get: (r) => datePart(r.createdAt) },
  { key: 'lastUpdatedAt', label: 'Last Updated', group: 'Dates', get: (r) => r.lastUpdatedAt },
  { key: 'lastTransitDate', label: 'Last Transit Date', group: 'Dates', get: (r) => datePart(r.lastUpdatedAt) },
  { key: 'lastTransitTime', label: 'Last Transit Time', group: 'Dates', get: (r) => timePart(r.lastUpdatedAt) },

  // Consignee
  { key: 'consigneeName', label: 'Customer Name', group: 'Consignee', needs: 'shipper', get: (r) => r.consigneeName },
  { key: 'consigneePhone', label: 'Phone', group: 'Consignee', needs: 'shipper', get: (r) => r.consigneePhone },
  { key: 'consigneeAddress', label: 'Address', group: 'Consignee', needs: 'shipper', get: (r) => r.consigneeAddress },
  { key: 'city', label: 'City', group: 'Consignee', get: (r) => r.city },
  { key: 'destination', label: 'Destination', group: 'Consignee', get: (r) => r.city },
  { key: 'receiverName', label: 'Received By', group: 'Consignee', get: (r) => r.receiverName },
  { key: 'handoverName', label: 'Handed Over To', group: 'Consignee', get: (r) => r.handoverName },

  // Shipper / pickup
  { key: 'shipperName', label: 'Shipper', group: 'Shipper / pickup', needs: 'shipper', get: (r) => r.shipperName },
  { key: 'shipperPhone', label: 'Shipper Phone', group: 'Shipper / pickup', needs: 'shipper', get: (r) => r.shipperPhone },
  { key: 'shipperAddress', label: 'Pickup Address', group: 'Shipper / pickup', needs: 'shipper', get: (r) => r.shipperAddress },
  { key: 'origin', label: 'Origin', group: 'Shipper / pickup', needs: 'shipper', get: (r) => r.shipperCity },

  // Money
  { key: 'paymentType', label: 'Payment Type', group: 'Money', get: (r) => r.paymentType },
  { key: 'codAmount', label: 'COD', group: 'Money', total: true, get: (r) => num(r.codAmount) },
  { key: 'cashCollected', label: 'COD Received', group: 'Money', total: true, get: (r) => num(r.cashCollected) },
  { key: 'dutiesTax', label: 'Duties & Tax', group: 'Money', needs: 'duties', total: true, get: (r) => num(r.dutiesTax) },
  { key: 'operationalAddon', label: 'Operational Addon', group: 'Money', needs: 'duties', total: true, get: (r) => num(r.operationalAddon) },
  { key: 'totalCollected', label: 'Total Collected (COD+Duties)', group: 'Money', needs: 'duties', total: true, get: (r) => num(r.totalCollected) },
  { key: 'collectionFlow', label: 'Collection Flow', group: 'Money', needs: 'custody', get: (r) => r.collectionFlow },

  // Accounts semantics: COD only counts once the order is delivered (as the accounts report always did)
  { key: 'codDelivered', label: 'COD', group: 'Accounts (delivered only)', total: true, get: (r) => (isDelivered(r) ? num(r.codAmount) : 0) },
  { key: 'cashDelivered', label: 'Cash Collected', group: 'Accounts (delivered only)', total: true, get: (r) => (isDelivered(r) ? num(r.cashCollected) : '') },
  { key: 'cashDifference', label: 'Cash Difference', group: 'Accounts (delivered only)', total: true,
    get: (r) => (isDelivered(r) ? (Number(r.codAmount) || 0) - (Number(r.cashCollected) || 0) : 0) },

  // Driver
  { key: 'driverName', label: 'Driver', group: 'Driver', needs: 'driver', get: (r) => r.driverName || (r.driverId ? `#${r.driverId}` : '') },
  { key: 'driverId', label: 'Driver ID', group: 'Driver', get: (r) => r.driverId },
  { key: 'driverTeam', label: 'Driver Team', group: 'Driver', needs: 'driver', get: (r) => r.driverTeam },
  { key: 'driverZone', label: 'Driver Zone', group: 'Driver', needs: 'driver', get: (r) => r.driverZone },
  { key: 'updatedBy', label: 'Updated By', group: 'Driver', needs: 'updatedBy', get: (r) => r.updatedBy },

  // Shipment
  { key: 'weight', label: 'Overall Weight', group: 'Shipment', total: true, get: (r) => num(r.weight) },
  { key: 'itemDescription', label: 'Item Description', group: 'Shipment', get: (r) => r.itemDescription },
  { key: 'warehouse', label: 'Warehouse', group: 'Shipment', get: (r) => r.warehouse },
  { key: 'homebaseId', label: 'Homebase', group: 'Shipment', get: (r) => r.homebaseId },
  { key: 'comments', label: 'Comments', group: 'Shipment', get: (r) => r.comments },

  // Items - declared by the partner when the order was created; blank when they did not send them
  { key: 'itemSummary', label: 'Item Summary', group: 'Items', get: (r) => r.itemSummary },
  { key: 'orderValue', label: 'Item Value', group: 'Items', total: true, get: (r) => num(r.orderValue) },
  { key: 'isJewellery', label: 'Is Jewellery', group: 'Items', get: (r) => yesNo(r.isJewellery) },

  // Billing
  { key: 'cxSettledStatus', label: 'CX Settled', group: 'Billing', get: (r) => r.cxSettledStatus },
  { key: 'cxSettledDate', label: 'CX Settled Date', group: 'Billing', get: (r) => r.cxSettledDate },
  { key: 'billedStatus', label: 'Billed Status', group: 'Billing', get: (r) => r.billedStatus },
  { key: 'billedDate', label: 'Billed Date', group: 'Billing', get: (r) => r.billedDate },
  { key: 'billedAmount', label: 'Billed Amount', group: 'Billing', total: true, get: (r) => num(r.billedAmount) },
  { key: 'billedCurrency', label: 'Billed Currency', group: 'Billing', get: (r) => r.billedCurrency },

  // Customer requests
  { key: 'actionCode', label: 'Action Code', group: 'Customer requests', get: (r) => r.actionCode },
  { key: 'actionDescription', label: 'Action Description', group: 'Customer requests', get: (r) => r.actionDescription },
  { key: 'dateChange', label: 'Date Change', group: 'Customer requests', get: (r) => yesNo(r.dateChange) },
  { key: 'addressChange', label: 'Address Change', group: 'Customer requests', get: (r) => yesNo(r.addressChange) },
  { key: 'blockDelivery', label: 'Delivery Blocked', group: 'Customer requests', get: (r) => yesNo(r.blockDelivery) },

  // Kept only so the accounts layout keeps its historical column positions; nothing stores them.
  { key: 'srvtype', label: 'SRVTYPE', group: 'Legacy (always blank)', get: () => '' },
  { key: 'toc', label: 'TOC', group: 'Legacy (always blank)', get: () => '' },
];

export const FIELD_BY_KEY = FIELDS.reduce((m, f) => { m[f.key] = f; return m; }, {});

/**
 * Presets = ordered [fieldKey, header?] lists. `operations` and `accounts` reproduce the two files
 * the teams already work with, header for header.
 */
export const PRESETS = [
  {
    id: 'operations',
    name: 'Operations',
    icon: 'fa-truck',
    description: 'The ops team’s file: customer, shipper, COD, driver, team & zone',
    columns: [
      ['awb', 'AWB'], ['businessRefNo', 'Client Ref'], ['consigneeName', 'Customer Name'], ['consigneePhone', 'Phone'],
      ['consigneeAddress', 'Address'], ['city', 'City'], ['shipperName', 'Shipper'], ['shipperPhone', 'Shipper Phone'],
      ['paymentType', 'Payment Type'], ['codAmount', 'COD'], ['cashCollected', 'COD Received'], ['driverName', 'Driver'],
      ['driverTeam', 'Driver Team'], ['driverZone', 'Driver Zone'], ['status', 'Status'], ['legType', 'Type'],
      ['orderDate', 'Assigned Date'], ['itemSummary', 'Item Summary'], ['orderValue', 'Item Value'],
      ['isJewellery', 'Is Jewellery'],
    ],
  },
  {
    id: 'accounts',
    name: 'Accounts',
    icon: 'fa-calculator',
    description: 'The accounts sheet: COD on delivered orders, duties, collection flow',
    totals: true,
    columns: [
      ['businessRefNo', 'CustawbNo'], ['awb', 'TimexAWB'], ['lastTransitDate', 'Last Transit Date'],
      ['createdDate', 'Order Creation Date'], ['status', 'Task Status'], ['consigneeName', 'Customer Name'], ['city', 'City'],
      ['shipperName', 'Shipper'], ['driverName', 'Driver'], ['driverTeam', 'Driver Team'], ['driverZone', 'Driver Zone'],
      ['businessRefNo', 'AWB'], ['weight', 'Weight'], ['destination', 'Destination'], ['status', 'Status'], ['legType', 'Type'],
      ['assignedDate', 'Assigned Date'], ['srvtype', 'SRVTYPE'], ['toc', 'TOC'], ['lastTransitTime', 'Last Transit Time'],
      ['driverName', 'Courier'], ['driverTeam', 'Courier team'], ['driverZone', 'Courier zone'], ['consigneeName', 'Consignee'],
      ['paymentType', 'Payment Type'], ['codDelivered', 'COD'], ['cashDelivered', 'Cash Collected'],
      ['cashDifference', 'Cash Difference'], ['updatedBy', 'Updated By'], ['dutiesTax', 'Duties & Tax'],
      ['operationalAddon', 'Operational Addon'], ['totalCollected', 'Total Collected (COD+Duties)'],
      ['collectionFlow', 'Collection Flow'], ['scannedAt', 'Scan Time'], ['masterAwbNumber', 'Multishipment AWB'],
      ['childAwbs', 'Child AWBs'], ['weight', 'Overall Weight'], ['projectName', 'Project'], ['serviceTime', 'Service Time'],
      ['itemSummary', 'Item Summary'], ['orderValue', 'Item Value'], ['isJewellery', 'Is Jewellery'],
    ],
  },
  {
    id: 'everything',
    name: 'Everything',
    icon: 'fa-th',
    description: 'Every available field, one column each',
    columns: FIELDS.filter((f) => f.group !== 'Legacy (always blank)').map((f) => [f.key]),
  },
];

let seq = 0;
const newId = () => `c${Date.now().toString(36)}${(seq++).toString(36)}`;

/** A column instance (fields can repeat under different headers, so columns carry their own id). */
export const makeColumn = (key, label) => ({ id: newId(), key, label: label || FIELD_BY_KEY[key]?.label || key });

export const columnsFromPreset = (preset) =>
  (preset?.columns || []).filter(([key]) => FIELD_BY_KEY[key]).map(([key, label]) => makeColumn(key, label));

/** The server lookups the chosen columns need - sent as `include` so the server skips the rest. */
export const neededLookups = (columns) =>
  [...new Set(columns.map((c) => FIELD_BY_KEY[c.key]?.needs).filter(Boolean))];
